export interface InstallerDownloadProgress {
  phase: "downloading" | "verifying";
  loaded: number;
  total: number;
}

export interface InstallerDownloadOptions {
  onProgress?: (progress: InstallerDownloadProgress) => void;
  signal?: AbortSignal;
}

export interface InstallerDownloadResult {
  fileName: string;
  version: string;
}

let downloading = false;
const MAX_INSTALLER_SIZE = 512 * 1024 * 1024;
const IDLE_TIMEOUT = 60_000;

/** One-time recovery for shells without a working native updater. */
export async function downloadInstallerInApp(
  backend: string,
  options: InstallerDownloadOptions = {},
): Promise<InstallerDownloadResult> {
  if (downloading) throw new Error("Ya hay una descarga de Eriol en curso.");
  const base = new URL(backend.replace(/\/+$/, "") + "/");
  if (
    base.protocol !== "https:" &&
    !(
      base.protocol === "http:" &&
      ["localhost", "127.0.0.1", "[::1]"].includes(base.hostname)
    )
  ) {
    throw new Error("La descarga del instalador requiere HTTPS.");
  }
  downloading = true;
  const controller = new AbortController();
  const abort = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", abort, { once: true });
  if (options.signal?.aborted) abort();
  let timer: ReturnType<typeof setTimeout>;
  const resetTimeout = () => {
    clearTimeout(timer);
    timer = setTimeout(
      () =>
        controller.abort(
          new Error("La descarga no recibio datos durante 60 segundos."),
        ),
      IDLE_TIMEOUT,
    );
  };
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    resetTimeout();
    const metaResponse = await fetch(new URL("installer/meta/windows", base), {
      signal: controller.signal,
      credentials: "omit",
      cache: "no-store",
    });
    if (!metaResponse.ok)
      throw new Error(
        `No se pudo consultar el instalador (HTTP ${metaResponse.status}).`,
      );
    const meta = (await metaResponse.json()) as {
      version: string;
      fileSize: number;
      sha256: string;
    };
    if (
      !/^[0-9]+\.[0-9]+\.[0-9]+(?:[-+][0-9A-Za-z.-]+)*$/.test(meta.version) ||
      !Number.isSafeInteger(meta.fileSize) ||
      meta.fileSize <= 0 ||
      meta.fileSize > MAX_INSTALLER_SIZE ||
      !/^[a-f0-9]{64}$/i.test(meta.sha256)
    ) {
      throw new Error(
        "El servidor no publico metadatos validos para el instalador.",
      );
    }
    resetTimeout();
    const response = await fetch(new URL("installer/download/windows", base), {
      signal: controller.signal,
      credentials: "omit",
      cache: "no-store",
    });
    if (response.status !== 200 || !response.body)
      throw new Error(
        `No se pudo descargar el instalador (HTTP ${response.status}).`,
      );
    if (new URL(response.url || base.href).protocol !== base.protocol)
      throw new Error("La descarga redirigio a un protocolo no permitido.");
    reader = response.body.getReader();
    const chunks: BlobPart[] = [];
    let loaded = 0;
    let lastReport = 0;
    options.onProgress?.({
      phase: "downloading",
      loaded,
      total: meta.fileSize,
    });
    while (true) {
      const { done, value } = await reader.read();
      controller.signal.throwIfAborted();
      if (done) break;
      loaded += value.byteLength;
      if (loaded > meta.fileSize)
        throw new Error("El instalador supera el tamano publicado.");
      chunks.push(value as BlobPart);
      resetTimeout();
      if (Date.now() - lastReport >= 200) {
        options.onProgress?.({
          phase: "downloading",
          loaded,
          total: meta.fileSize,
        });
        lastReport = Date.now();
      }
    }
    clearTimeout(timer!);
    if (loaded !== meta.fileSize)
      throw new Error("La descarga del instalador quedo incompleta.");
    options.onProgress?.({ phase: "verifying", loaded, total: meta.fileSize });
    const blob = new Blob(chunks, { type: "application/octet-stream" });
    const digest = await crypto.subtle.digest(
      "SHA-256",
      await blob.arrayBuffer(),
    );
    const actualHash = Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
    controller.signal.throwIfAborted();
    if (actualHash !== meta.sha256.toLowerCase())
      throw new Error(
        "La verificacion del instalador fallo. Volve a descargarlo.",
      );
    const fileName = `Eriol-${meta.version}-Windows.exe`;
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.hidden = true;
      document.body.append(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
      }
    } finally {
      // Electron's download item owns the Blob once its request has started.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    return { fileName, version: meta.version };
  } finally {
    clearTimeout(timer!);
    options.signal?.removeEventListener("abort", abort);
    controller.abort();
    await reader?.cancel().catch(() => {});
    reader?.releaseLock();
    downloading = false;
  }
}
